import {
  CrmObject,
  CrmObjectTypeDefinition,
  CrmObjectTypeProperty,
  CrmSearchVariables,
  AssociationPreview,
} from 'marketing-platform-lib/types';
import { OBJECT_TYPE } from 'marketing-platform-lib/constants/crm';
import {
  getFilterGroups,
  makeCrmObjectSearchRequestName,
} from 'marketing-platform-lib/earlyRequester/crmObjectEarlyRequester';

export function hasProperty(crmObject: CrmObject, propertyName: string) {
  return Boolean(crmObject?.properties && propertyName in crmObject.properties);
}

/**
 * Returns the value of @propertyName in @crmObject.
 *
 * If @propertyName is not present:
 *   - returns undefined when @defaultValue is not provided.
 *   - returns @defaultValue otherwise.
 * @param crmObject The CRM object
 * @param propertyName The property name
 * @param defaultValue Optional default value if @propertyName is not found in @crmObject
 */
export function getPropertyValue(
  crmObject: CrmObject,
  propertyName: string,
  defaultValue?: CrmObjectTypeProperty['value']
) {
  if (hasProperty(crmObject, propertyName)) {
    return crmObject?.properties[propertyName].value;
  } else if (propertyName.startsWith('associatedObjectId')) {
    const associationNames = propertyName.match(/0-\d+/g);
    if (associationNames && associationNames.length > 0) {
      const associatedObjectType = associationNames.find(
        (name) =>
          crmObject?.associatedObjects?.[name] &&
          crmObject.associatedObjects?.[name]?.length > 0
      );
      if (associatedObjectType) {
        const associatedObjectId =
          crmObject?.associatedObjects?.[associatedObjectType]?.[0]?.objectId;
        const stringOrUndefinedAssociatedObjectId = associatedObjectId
          ? String(associatedObjectId)
          : undefined;

        return stringOrUndefinedAssociatedObjectId;
      }
    }
  } else if (propertyName.startsWith('associatedObjectTypeId')) {
    const associationNames = propertyName.match(/0-\d+/g);
    if (associationNames && associationNames.length > 0) {
      const associatedObjectType = associationNames.find(
        (name) =>
          crmObject?.associatedObjects?.[name] &&
          crmObject.associatedObjects?.[name]?.length > 0
      );
      if (associatedObjectType) {
        return crmObject?.associatedObjects?.[associatedObjectType]?.[0]
          ?.objectTypeId;
      }
    }
  }

  return defaultValue;
}

/**
 * Set property value and return a new CrmObject
 * @param crmObject The CRM Object.
 * @param propertyName Property name to be updated.
 * @param value Property value.
 */
export function setPropertyValue(
  crmObject: CrmObject,
  propertyName: string,
  value: CrmObjectTypeProperty['value']
): CrmObject {
  let result = {
    ...crmObject,
  };

  // Create property if it doesn't exist.
  if (!hasProperty(result, propertyName)) {
    result = {
      ...result,
      properties: {
        ...result.properties,
        [propertyName]: {
          value: '',
        },
      },
    };
  }

  result = {
    ...result,
    properties: {
      ...result.properties,
      [propertyName]: {
        ...result.properties?.[propertyName],
        value,
      },
    },
  };

  return result;
}

export function getAssociationPreviews(
  objectTypeId: string
): Record<string, AssociationPreview> | undefined {
  if (objectTypeId === OBJECT_TYPE.MEETING_EVENT) {
    return {
      '0-200': {
        includeReverseReferences: false,
        includeAllProperties: true,
        allPropertiesFetchMode: 'latest_version',
      },
      '0-188': {
        includeReverseReferences: false,
        includeAllProperties: true,
        allPropertiesFetchMode: 'latest_version',
      },
      '0-212': {
        includeReverseReferences: false,
        includeAllProperties: true,
        allPropertiesFetchMode: 'latest_version',
      },
      '0-226': {
        includeReverseReferences: false,
        includeAllProperties: true,
        allPropertiesFetchMode: 'latest_version',
      },
    };
  }

  return undefined;
}

/**
 * Convert @crmObject to a UISelect option object.
 *
 * For objects that have `hs_name` property:
 *   - propagate object id and `hs_name.value`.
 *
 * For objects that don't have `hs_name` property:
 *   - propagate object id and serialize all properties
 *
 */
export function convertToSelectOption(
  crmObject: CrmObject,
  typeDefinition?: CrmObjectTypeDefinition
) {
  const propertyValue = getPropertyValue(
    crmObject,
    (typeDefinition && typeDefinition.primaryDisplayLabelPropertyName) || ''
  );

  if (propertyValue) {
    return {
      text: propertyValue,
      value: crmObject.objectId,
    };
  }

  if ('hs_name' in crmObject.properties) {
    return {
      text: `${crmObject.properties.hs_name.value}`,
      value: crmObject.objectId,
    };
  }

  if (
    crmObject.objectTypeId === OBJECT_TYPE.PUBLISHING_TASK &&
    'hs_publishing_task_name' in crmObject.properties
  ) {
    return {
      text: `${crmObject.properties.hs_publishing_task_name.value}`,
      value: crmObject.objectId,
    };
  }

  /**
   * Serialize all properties for CRM objects that don't have @hs_name property
   */
  const propertiesArr = Object.keys(crmObject.properties).map((key) => ({
    name: key,
    value: crmObject.properties[key].value,
  }));

  return {
    text: JSON.stringify(propertiesArr),
    value: crmObject.objectId,
  };
}

export function getCrmSearchVariables(
  objectTypeId: string,
  objectId: number | string
) {
  const variables: CrmSearchVariables = {
    fetchParams: {
      count: 1,
      filterGroups: getFilterGroups(objectId, objectTypeId),
      objectTypeId,
      offset: 0,
      query: '',
      requestOptions: {
        allPropertiesFetchMode: 'latest_version',
        includeAllProperties: true,
      },
      associationPreviews: getAssociationPreviews(objectTypeId),
      sorts: [],
    },
    quickFetchName: makeCrmObjectSearchRequestName({ objectTypeId, objectId }),
  };

  return variables;
}

/**
 * Returns true if @objectTypeId is managed by the CRM team.
 */
export function isStandardCrmObject(objectTypeId: string) {
  return [
    OBJECT_TYPE.CONTACT.toString(),
    OBJECT_TYPE.COMPANY.toString(),
    OBJECT_TYPE.DEAL.toString(),
    OBJECT_TYPE.TICKET.toString(),
  ].includes(objectTypeId);
}
